/**
 * No Relief reminder — a popup 30 minutes before a counter goes unmanned, for the
 * calls nobody has ticked yet. It lists who to phone (the officer on each counter
 * until then) with a tick for each.
 *
 * Only ticking stops it: the ✕ just snoozes — the popup comes back every 2 minutes
 * until every call in it is ticked. A call falling due while it is snoozed shows
 * at once. Not shown to view-only users, outside the shift, while the full No
 * Relief list is open, or to a user who turned it off for this session
 * (utils/noReliefAlert — "Stop popping up" below, or Settings).
 */

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatCounterLabel } from '@/config/modeConfig';
import { NUM_SLOTS, getStartHour } from '@/config/modeConfig';
import { useNotification } from '@/contexts/NotificationContext';
import { useSession } from '@/contexts/SessionContext';
import { slotToHhmm } from '@/utils/partialTiming';
import type { NoReliefAckKind, OfficerMetadata } from '@/types/api';
import type { NoReliefGroup } from '@/utils/noRelief';
import { setNoReliefAlert } from '@/utils/noReliefAlert';
import {
  REMINDER_SNOOZE_MS, dueReminders, reminderKey, type DueReminderEntry,
} from '@/utils/noReliefReminder';
import { UI_SCALE, popupMaxH } from '@/utils/uiScale';

const CHECK_EVERY_MS = 10_000;

/** The live time as a fractional slot from the shift's left edge; null outside the shift. */
function liveSlot(shiftType: string | undefined, now: Date): number | null {
  const startHour = getStartHour(shiftType ?? 'morning');
  const minutes = ((now.getHours() - startHour + 24) % 24) * 60 + now.getMinutes() + now.getSeconds() / 60;
  const slot = minutes / 15;
  return slot >= 0 && slot < NUM_SLOTS ? slot : null;
}

function officerName(id: string, meta: OfficerMetadata | undefined): string {
  const parts = [meta?.deployment, meta?.name].filter(Boolean).join(' - ');
  return parts || id;
}

export function NoReliefReminder({
  groups,
  counterMatrix,
  officerMetadata,
  operationMode,
  shiftType,
  suppressed,
  onAck,
}: {
  groups: NoReliefGroup[];
  counterMatrix: string[][][] | undefined;
  officerMetadata: Record<string, OfficerMetadata> | undefined;
  operationMode: string | undefined;
  shiftType: string | undefined;
  /** True while something else already has the user's attention (the No Relief list is open). */
  suppressed: boolean;
  onAck: (counter: number, slot: number, kind: NoReliefAckKind) => Promise<void>;
}) {
  const { canEdit, accessMode, sessionId } = useSession();
  const { notifyInfo } = useNotification();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = () => setNow(new Date());
    const id = window.setInterval(tick, CHECK_EVERY_MS);
    // Phones suspend timers in the background — catch up the moment the tab returns.
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  const due = useMemo(
    () => dueReminders(groups, counterMatrix, accessMode === 'browse' ? null : liveSlot(shiftType, now)),
    [groups, counterMatrix, shiftType, now, accessMode],
  );

  // Closed with ✕: back after the snooze, or at once for a call that was not in it when closed.
  const [snooze, setSnooze] = useState<{ until: number; keys: Set<string> } | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState('');

  const dueKeys = due.flatMap((g) => g.entries.map((e) => reminderKey(e.entry)));
  const visible = canEdit && !suppressed && dueKeys.length > 0
    && (!snooze || now.getTime() >= snooze.until || dueKeys.some((k) => !snooze.keys.has(k)));
  if (!visible) return null;

  const startHour = getStartHour(shiftType ?? 'morning');
  const close = () => {
    setSnooze({ until: Date.now() + REMINDER_SNOOZE_MS, keys: new Set(dueKeys) });
    notifyInfo('Snoozed for 2 mins', false);
  };

  const tick = async (item: DueReminderEntry) => {
    const key = reminderKey(item.entry);
    if (busyKey) return;
    setBusyKey(key);
    setError('');
    try {
      await onAck(item.entry.counter, item.entry.slot, 'no_relief');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save the tick.');
    } finally {
      setBusyKey(null);
    }
  };

  return createPortal(
    <div
      role="alertdialog"
      aria-label="No relief reminder"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        style={{ background: 'white', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          width: '100%', maxWidth: 460, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          zoom: UI_SCALE, maxHeight: popupMaxH(80), border: '2px solid #f87171' }}
      >
        <div style={{ padding: '12px 18px', background: '#fee2e2', borderBottom: '1px solid #fecaca', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#991b1b' }}>🚫 Call: no relief coming up</span>
          <button
            onClick={close}
            aria-label="Snooze 2 minutes"
            title="Snooze 2 minutes"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0 }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="3" y1="3" x2="13" y2="13" /><line x1="13" y1="3" x2="3" y2="13" />
            </svg>
          </button>
        </div>

        <div style={{ padding: '8px 18px', borderBottom: '1px solid #e5e7eb', fontSize: 12, color: '#64748b', flexShrink: 0 }}>
          ✕ snoozes for 2 minutes · Tick to dismiss notification
          {sessionId && (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => {
                  setNoReliefAlert(sessionId, 'badge');
                  notifyInfo('No Relief popup off for this session — the No Relief button still counts the calls. Change it in Settings.', false);
                }}
                style={{ background: 'none', border: 'none', padding: 0, color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}
              >
                Stop popping up for this session
              </button>
            </>
          )}
        </div>

        {error && <div style={{ padding: '6px 18px', fontSize: 12, color: '#dc3545', flexShrink: 0 }}>{error}</div>}

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {due.map((group) => (
            <div key={group.slot} style={{ padding: '10px 18px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 13, color: '#1e293b', marginBottom: 6 }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15 }}>{slotToHhmm(group.slot, startHour)}</span>
                <span style={{ marginLeft: 8, color: group.minutesAway > 0 ? '#64748b' : '#b91c1c', fontWeight: 600 }}>
                  {group.minutesAway > 0 ? `in ${group.minutesAway} min` : group.minutesAway === 0 ? 'now' : `${-group.minutesAway} min ago`}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {group.entries.map((item) => {
                  const key = reminderKey(item.entry);
                  const label = formatCounterLabel(item.entry.counter + 1, operationMode);
                  return (
                    <label
                      key={key}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 8,
                        border: '1px solid #f87171', background: '#fee2e2', color: '#991b1b', cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={false}
                        disabled={busyKey !== null}
                        onChange={() => void tick(item)}
                        aria-label={`Dismiss: officer on ${label} told`}
                        style={{ margin: 0, width: 18, height: 18, accentColor: '#f87171', cursor: 'inherit' }}
                      />
                      <span style={{ fontWeight: 700, fontSize: 14, minWidth: 44 }}>{label}</span>
                      <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
                        {item.officerIds.length > 0
                          ? item.officerIds.map((id) => officerName(id, officerMetadata?.[id])).join(', ')
                          : 'officer on this counter'}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
